export const CONTEST_CAMPAIGN_ID = 'aws_oct_2026';
export const FEATURED_CONTEST_ID = '47';

const STORAGE_KEY = 'spotlight_contest_campaign_context_v1';
const CONTEXT_TTL_MS = 4 * 60 * 60 * 1000;
const UTM_FIELDS = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmContent', 'utm_content'],
  ['utmTerm', 'utm_term'],
];

const getEntryStartedStorageKey = (contestId) => `${STORAGE_KEY}:entry_started:${contestId}`;

const readStoredContext = (storage) => {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.createdAt || Date.now() - parsed.createdAt > CONTEXT_TTL_MS) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const writeStoredContext = (storage, payload) => {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Campaign continuation still works through the URL when storage is unavailable.
  }
};

const removeStoredContext = (storage) => {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export const getUtmAttributionFromSearch = (search = '') => {
  const params = new URLSearchParams(search);

  return UTM_FIELDS.reduce((result, [contextKey, queryKey]) => ({
    ...result,
    [contextKey]: params.get(queryKey) || null,
  }), {});
};

export const getContestAttributionProperties = (context = null) => ({
  utm_source: context?.utmSource || null,
  utm_medium: context?.utmMedium || null,
  utm_campaign: context?.utmCampaign || null,
  utm_content: context?.utmContent || null,
  utm_term: context?.utmTerm || null,
});

const getCurrentUtmAttribution = () => {
  if (typeof window === 'undefined') return getUtmAttributionFromSearch('');
  return getUtmAttributionFromSearch(window.location.search);
};

const mergeUtmAttribution = (...contexts) => UTM_FIELDS.reduce(
  (result, [contextKey]) => ({
    ...result,
    [contextKey]: contexts.find((context) => context?.[contextKey])?.[contextKey] || null,
  }),
  {},
);

const appendUtmParams = (params, context = {}) => {
  UTM_FIELDS.forEach(([contextKey, queryKey]) => {
    if (context?.[contextKey]) params.set(queryKey, context[contextKey]);
  });
};

export const hasTrackedContestEntryStarted = (contestId = FEATURED_CONTEST_ID) => {
  if (typeof window === 'undefined') return false;

  try {
    return Boolean(window.sessionStorage.getItem(getEntryStartedStorageKey(`${contestId}`)));
  } catch {
    return false;
  }
};

export const markContestEntryStartedTracked = (contestId = FEATURED_CONTEST_ID) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(getEntryStartedStorageKey(`${contestId}`), `${Date.now()}`);
  } catch {
    // Ignore storage failures; the in-component guard still prevents duplicate sends.
  }
};

export const sanitizeInternalPath = (value) => {
  if (!value || typeof value !== 'string') return null;

  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }

  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('://')) {
    return null;
  }

  return decoded;
};

export function getPendingContestCampaignContext() {
  if (typeof window === 'undefined') return null;

  const parsed = readStoredContext(window.sessionStorage)
    || readStoredContext(window.localStorage);
  if (!parsed) return null;

  const returnPath = sanitizeInternalPath(parsed.returnPath);
  if (!returnPath) {
    removeStoredContext(window.sessionStorage);
    removeStoredContext(window.localStorage);
    return null;
  }

  return {
    ...parsed,
    contestId: `${parsed.contestId || FEATURED_CONTEST_ID}`,
    placement: parsed.placement || null,
    source: parsed.source || 'contest_landing_page',
    creativeId: parsed.creativeId || null,
    ...mergeUtmAttribution(parsed),
    returnPath,
  };
}

export const buildContestReturnPath = ({
  contestId = FEATURED_CONTEST_ID,
  placement = null,
  source = 'contest_landing_page',
  creativeId,
  ...attribution
} = {}) => {
  const params = new URLSearchParams();
  const resolvedAttribution = mergeUtmAttribution(
    attribution,
    getCurrentUtmAttribution(),
    getPendingContestCampaignContext(),
  );

  params.set('contest_id', `${contestId}`);
  params.set('campaign_id', CONTEST_CAMPAIGN_ID);
  if (placement) params.set('placement', placement);
  if (source) params.set('source', source);
  if (creativeId) params.set('creative_id', creativeId);
  appendUtmParams(params, resolvedAttribution);

  return `/contests/${contestId}?${params.toString()}`;
};

export const buildContestAuthPath = (authPath, context = {}) => {
  const resolvedAttribution = mergeUtmAttribution(
    context,
    getCurrentUtmAttribution(),
    getPendingContestCampaignContext(),
  );
  const fullContext = { ...context, ...resolvedAttribution };
  const returnPath = buildContestReturnPath(fullContext);
  const params = new URLSearchParams();

  params.set('contest_id', `${context.contestId || FEATURED_CONTEST_ID}`);
  params.set('campaign_id', CONTEST_CAMPAIGN_ID);
  if (context.placement) params.set('placement', context.placement);
  params.set('source', context.source || 'contest_landing_page');
  if (context.creativeId) params.set('creative_id', context.creativeId);
  appendUtmParams(params, resolvedAttribution);
  params.set('return_to', returnPath);

  return `${authPath}?${params.toString()}`;
};

export const persistContestCampaignContext = (context = {}) => {
  if (typeof window === 'undefined') return null;

  const existing = getPendingContestCampaignContext();
  const attribution = mergeUtmAttribution(
    context,
    getCurrentUtmAttribution(),
    existing,
  );
  const fullContext = { ...context, ...attribution };
  const payload = {
    campaignId: CONTEST_CAMPAIGN_ID,
    contestId: `${context.contestId || existing?.contestId || FEATURED_CONTEST_ID}`,
    placement: context.placement || existing?.placement || null,
    source: context.source || existing?.source || 'contest_landing_page',
    creativeId: context.creativeId || existing?.creativeId || null,
    ...attribution,
    returnPath: buildContestReturnPath(fullContext),
    createdAt: Date.now(),
  };

  writeStoredContext(window.sessionStorage, payload);
  // OAuth is a full-page round trip. localStorage is a short-lived fallback in case
  // a browser/WebView drops sessionStorage while leaving and returning to Spotlight.
  writeStoredContext(window.localStorage, payload);

  return payload;
};

export const getActiveContestCampaignContext = (contestId = FEATURED_CONTEST_ID) => {
  const normalizedContestId = `${contestId}`;
  const pending = getPendingContestCampaignContext();

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('campaign_id') === CONTEST_CAMPAIGN_ID) {
      const queryContestId = `${params.get('contest_id') || normalizedContestId}`;
      if (queryContestId === normalizedContestId) {
        const queryContext = {
          contestId: queryContestId,
          placement: params.get('placement') || pending?.placement || null,
          source: params.get('source') || pending?.source || 'contest_landing_page',
          creativeId: params.get('creative_id') || pending?.creativeId || null,
          ...mergeUtmAttribution(getUtmAttributionFromSearch(window.location.search), pending),
        };

        return persistContestCampaignContext(queryContext);
      }
    }
  }

  if (!pending || `${pending.contestId}` !== normalizedContestId) return null;
  return pending;
};

export const clearPendingContestCampaignContext = () => {
  if (typeof window === 'undefined') return;

  removeStoredContext(window.sessionStorage);
  removeStoredContext(window.localStorage);
};

export const getContestRedirectPath = ({ search = '', stateRedirectPath } = {}) => {
  const params = new URLSearchParams(search);
  const queryReturnPath = sanitizeInternalPath(params.get('return_to'));
  if (queryReturnPath) return queryReturnPath;

  const safeStatePath = sanitizeInternalPath(stateRedirectPath);
  if (safeStatePath) return safeStatePath;

  return getPendingContestCampaignContext()?.returnPath || null;
};

export const isContestCampaignSearch = (search = '') => {
  const params = new URLSearchParams(search);
  return params.get('campaign_id') === CONTEST_CAMPAIGN_ID;
};
